// Retry policy for the SSE resume stream. Pure functions + typed errors so
// the reconnect generator (createResumeStream) and unit tests share one
// classification surface. Numbers picked to give ~60s total budget across
// 6 attempts: 1s → 2s → 4s → 8s → 16s → 30s (cap), with ±25% decorrelated jitter.

export const MAX_RETRY_ATTEMPTS = 6;
export const BASE_DELAY_MS = 1000;
const CAP_DELAY_MS = 30000;

// Polly-style decorrelated jitter: next delay is sampled from
// [base, prevDelay * 3] capped at CAP_DELAY_MS. Naturally desynchronizes
// retries across clients during a backend restart so we don't thundering-herd
// the server the moment it comes back up.
export function decorrelatedJitter(prevDelay: number): number {
  const min = BASE_DELAY_MS;
  const max = Math.min(CAP_DELAY_MS, Math.max(prevDelay * 3, BASE_DELAY_MS * 2));
  return Math.floor(min + Math.random() * (max - min));
}

const EXPLICIT_RETRYABLE_STATUSES = new Set([408, 429]);

export function isRetryableHttpStatus(status: number): boolean {
  if (status >= 500 && status <= 599) return true;
  return EXPLICIT_RETRYABLE_STATUSES.has(status);
}

export class HttpRetryableError extends Error {
  constructor(public status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = "HttpRetryableError";
  }
}

export class HttpNonRetryableError extends Error {
  constructor(public status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = "HttpNonRetryableError";
  }
}

export class MaxRetriesExceededError extends Error {
  constructor(public attempts: number) {
    super(`Reconnect failed after ${attempts} attempts`);
    this.name = "MaxRetriesExceededError";
  }
}

// Classify any throw from the resume generator. The retry loop calls this on
// every catch — anything we deem retryable triggers a backoff + new attempt;
// everything else short-circuits to MessageError. Keep tight: when in doubt,
// don't retry — a runaway loop on a bug is worse than surfacing the error.
export function isRetryableError(err: unknown): boolean {
  if (err instanceof HttpRetryableError) return true;
  if (err instanceof HttpNonRetryableError) return false;
  if (err instanceof MaxRetriesExceededError) return false;
  if (err instanceof Error) {
    if (err.name === "AbortError") return false;
    // fetch() raises TypeError("Failed to fetch") on network errors.
    if (err instanceof TypeError) return true;
  }
  return false;
}
